import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { Formik, Form } from 'formik';
import {
  Modal,
  Box,
  Button,
  Text,
  Select,
} from '@rsos/components/capstone/base';
import {
  ModalHeader,
  ModalBody,
  ModalFooter,
} from '@rsos/components/capstone/base/Modal/ModalComponents';
import InputText from '@rsos/components/capstone/base/form/inputs/InputText';
import { createAppUser } from '@rsos/sinatra';
import { checkEmailExists } from '@rsos/utils';
import { notifySuccess, notifyError } from '@rsos/utils/toastUtils';
import { GENERIC_REQUEST_ERROR_MESSAGE } from '../../../constants/defaultErrorMessages';
import { trackAddUserSuccess } from '../usersTrackings';
import { validationSchema } from '../validationSchema';
import { Label, ButtonContainer } from './Modal.styles';

const AddUserModal = ({
  orgID,
  isOpened,
  allRoles,
  application,
  currentPSAPUsers,
  toggleAddUserModal,
}) => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);

  const buildOptions = state => ({
    label: state.name,
    value: state.name,
  });

  const initialValues = {
    first_name: '',
    last_name: '',
    email: '',
    role: 'Select',
  };

  const handleRoleChange = (selectedOption, values, callback) => {
    values['role'] = selectedOption.value;
    setSelectedRole(selectedOption);
    callback('role', selectedOption, true);
  };

  const sendInvite = (orgID, userInfo) => {
    dispatch(createAppUser(orgID, application, userInfo))
      .then(() => {
        trackAddUserSuccess();
        notifySuccess('Invite has been sent.');
      })
      .catch(action => {
        const errorMessage = action?.error?.message;
        notifyError(
          errorMessage ? errorMessage : GENERIC_REQUEST_ERROR_MESSAGE,
        );
      })
      .finally(() => setIsLoading(false));
    toggleAddUserModal();
  };

  const handleSubmit = user => {
    setIsLoading(true);
    const userInfo = {
      application: application,
      email: user.email,
      first_name: user.first_name,
      last_name: user.last_name,
      role: user.role.value,
    };
    let isEmailExist;
    if (userInfo.email && currentPSAPUsers) {
      isEmailExist = checkEmailExists(currentPSAPUsers, userInfo.email);
      if (isEmailExist) {
        notifyError(
          `This user already exists. Please verify the user's email address and try again.`,
        );
      } else {
        sendInvite(orgID, userInfo);
      }
    }
  };

  const checkBtnDisabled = ({ first_name, last_name, email, role }, errors) => {
    if (first_name && last_name && email && role !== 'Select') {
      const hasError = Object.entries(errors).length;
      if (!hasError) {
        return false;
      }
    }
    return true;
  };

  return (
    <Modal
      id="edit-user-modal"
      isOpened={isOpened}
      onClose={toggleAddUserModal}
      width={400}
    >
      <ModalHeader onClose={toggleAddUserModal}>
        <Box mx="5px">
          <Text size="xlarge" color="secondaryText">
            Add User
          </Text>
        </Box>
      </ModalHeader>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        validateOnMount={true}
        onSubmit={values => {
          handleSubmit(values);
        }}
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          setFieldValue,
        }) => (
          <Form id="addUserModal">
            <ModalBody pt="19px" justify="center" noFooter>
              <Box mx="5px">
                <Box pb={4}>
                  <Label>
                    <Text>First Name*</Text>
                  </Label>
                  <InputText
                    shouldErrorTakeSpace
                    id="first_name"
                    name="first_name"
                    type="text"
                    value={values.first_name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    setFieldValue={setFieldValue}
                    error={
                      errors.first_name &&
                      (!!values.first_name || touched.first_name)
                        ? errors.first_name
                        : null
                    }
                  />
                </Box>
                <Box pb={4}>
                  <Label>
                    <Text>Last Name*</Text>
                  </Label>
                  <InputText
                    shouldErrorTakeSpace
                    id="last_name"
                    name="last_name"
                    type="text"
                    value={values.last_name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    setFieldValue={setFieldValue}
                    error={
                      errors.last_name &&
                      (!!values.last_name || touched.last_name)
                        ? errors.last_name
                        : null
                    }
                  />
                </Box>
                <Box pb={4}>
                  <Label>
                    <Text>Email*</Text>
                  </Label>
                  <InputText
                    shouldErrorTakeSpace
                    id="email"
                    name="email"
                    type="email"
                    value={values.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    setFieldValue={setFieldValue}
                    error={
                      errors.email && (!!values.email || touched.email)
                        ? errors.email
                        : null
                    }
                  />
                </Box>
                <Box pb={'27px'}>
                  <Label>
                    <Text>Role*</Text>
                  </Label>
                  <Select
                    options={allRoles.map(buildOptions)}
                    placeholder="Select"
                    isClearable={false}
                    isSearchable={false}
                    onChange={selectedOption => {
                      handleRoleChange(selectedOption, values, setFieldValue);
                    }}
                    value={selectedRole}
                  />
                </Box>
              </Box>
            </ModalBody>
            <ModalFooter justify="center">
              <ButtonContainer horizontal>
                <Button grow btnType="tertiary" onClick={toggleAddUserModal}>
                  Cancel
                </Button>
                <Button
                  grow
                  type="submit"
                  btnType="primary"
                  form="addUserModal"
                  disabled={isLoading || checkBtnDisabled(values, errors)}
                >
                  Confirm
                </Button>
              </ButtonContainer>
            </ModalFooter>
          </Form>
        )}
      </Formik>
    </Modal>
  );
};

export default AddUserModal;
